import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getMemberCoaches,
    addMemberCoach,
    removeMemberCoach,
    updatePrimaryMemberCoach,
} from './api';
import { memberCoachKeys } from './keys';
import { sharedMembershipKeys } from '../sharedMembership/keys';

/** 배정된 강사 목록 (centerId 전달 시 해당 센터로 스코핑 + 방어 필터 적용) */
export const useMemberCoaches = ({
    memberId,
    centerId,
    enabled = true,
}: {
    memberId: string;
    centerId?: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: memberCoachKeys.list(memberId, centerId),
        queryFn: () => getMemberCoaches(memberId, centerId),
        enabled: enabled && !!memberId,
        select: (data) => {
            const coaches = data?.data?.connectedCoaches ?? [];
            if (!centerId) return coaches;
            // 이중 안전망: 명시적으로 다른 센터인 강사만 숨김.
            // centerId 가 null/미확정이거나 현재 센터와 일치하면 유지(정상 강사를 잘못 숨기지 않음).
            return coaches.filter((c) => c.centerId == null || c.centerId === centerId);
        },
    });

// 회원 단위로 캐싱된 강사별 수강권 쿼리들(['getAllMembershipForMemberQuery', coachId, memberId])
// 을 한꺼번에 stale 처리. 강사 추가/해제 시 백엔드가 그 강사의 활성 수강권을 만료시킬 수 있고,
// 같은 강사를 재배정하면 캐시 hit 으로 만료 전 데이터가 다시 표시되는 문제를 막기 위함.
const invalidateMembershipQueriesForMember = (qc: ReturnType<typeof useQueryClient>, memberId: string) => {
    qc.invalidateQueries({
        queryKey: ['getAllMembershipForMemberQuery'],
        predicate: (query) => query.queryKey[2] === memberId,
    });
};

/** 강사 추가 배정 */
export const useAddMemberCoach = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, coachId, centerId }: { memberId: string; coachId: string; centerId?: string }) =>
            addMemberCoach(memberId, coachId, centerId),
        onSuccess: (_, { memberId }) => {
            qc.invalidateQueries({ queryKey: memberCoachKeys.list(memberId) });
            qc.invalidateQueries({ queryKey: ['publicMember'] });
            invalidateMembershipQueriesForMember(qc, memberId);
            /**
             * 강사 연결은 이 회원의 '사용 권리'를 바꾼다 — 연결 전에는 서버가 404로 막고,
             * 연결 후에야 수강권이 보인다. 이 캐시를 비우지 않으면 staleTime(30초) 동안
             * 수업 등록 화면·회원 프로필이 "연결했는데 여전히 수강권 없음"으로 보인다.
             */
            qc.invalidateQueries({ queryKey: sharedMembershipKeys.all });
        },
    });
};

/** 대표 강사 변경 */
export const useUpdatePrimaryMemberCoach = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, coachId, centerId }: { memberId: string; coachId: string; centerId?: string }) =>
            updatePrimaryMemberCoach(memberId, coachId, centerId),
        onSuccess: (_, { memberId }) => {
            qc.invalidateQueries({ queryKey: memberCoachKeys.list(memberId) });
            qc.invalidateQueries({ queryKey: ['publicMember'] });
            invalidateMembershipQueriesForMember(qc, memberId);
            /**
             * 강사 연결은 이 회원의 '사용 권리'를 바꾼다 — 연결 전에는 서버가 404로 막고,
             * 연결 후에야 수강권이 보인다. 이 캐시를 비우지 않으면 staleTime(30초) 동안
             * 수업 등록 화면·회원 프로필이 "연결했는데 여전히 수강권 없음"으로 보인다.
             */
            qc.invalidateQueries({ queryKey: sharedMembershipKeys.all });
        },
    });
};

/** 강사 제거 */
export const useRemoveMemberCoach = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, coachId, centerId }: { memberId: string; coachId: string; centerId?: string }) =>
            removeMemberCoach(memberId, coachId, centerId),
        onSuccess: (_, { memberId }) => {
            qc.invalidateQueries({ queryKey: memberCoachKeys.list(memberId) });
            qc.invalidateQueries({ queryKey: ['publicMember'] });
            invalidateMembershipQueriesForMember(qc, memberId);
            /**
             * 강사 연결은 이 회원의 '사용 권리'를 바꾼다 — 연결 전에는 서버가 404로 막고,
             * 연결 후에야 수강권이 보인다. 이 캐시를 비우지 않으면 staleTime(30초) 동안
             * 수업 등록 화면·회원 프로필이 "연결했는데 여전히 수강권 없음"으로 보인다.
             */
            qc.invalidateQueries({ queryKey: sharedMembershipKeys.all });
        },
    });
};
